import { Box, Button, Card, CardContent, CardHeader, Checkbox, Divider, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, 
  Grid, Stack, Drawer, Switch, Tab, Tabs, TextField, Typography, useMediaQuery, alpha } from '@mui/material';
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react'
import { RoleResponseDto, UpdateUserInput, UserResponseDto, UserStatus } from '@/gen/api/user';
import { RecPatch } from '@/dataProvider/tools';
import { useEditContext, useRecordContext, useRefresh } from 'ra-core';
import Edit from '@/controller/Edit';
import { useDefaultSavedHandlers } from '@/controller/defaultSavedHandlers';
import { useSimpleSaveAction } from '@/controller/SaveContextProvider';
import { TabContext, TabPanel } from '@mui/lab';
import { DataAccessRoleResponseDto } from '@/gen/api/governance';
import retrieveAxiosErr from '@/utils/retrieveAxiosErr';


export interface EditUserProps {
  currentUserId: number|undefined,
  user: UserResponseDto & RecPatch;
  roles: RoleResponseDto[];
  dataAccessRoles: DataAccessRoleResponseDto[];
}

export interface EditUserBodyProps extends EditUserProps {
  open: boolean;
  setOpen: (x:boolean) => void;
}

const EditUser: FC<EditUserProps> = (props) => {
  const { user, roles, dataAccessRoles, currentUserId } = props
  const [open, setOpen] = useState(false)
  const refresh = useRefresh()

  const handlers = useDefaultSavedHandlers('Update User')
  handlers.onSuccess.merge(useCallback(() => {
    setOpen(false)
    refresh()
  }, [refresh]))

  return (
    <Edit id={user.id} resource={open ? 'settings/organization/user-manage' : 'noop'} mutationMode="pessimistic" basePath="" {...handlers} >
      <EditUserBody user={user} roles={roles} dataAccessRoles={dataAccessRoles} currentUserId={currentUserId} open={open} setOpen={setOpen} />
    </Edit>
  )
}

interface RolesSelection {
  admin: Record<number, boolean>;
  custom: Record<number, boolean>;
}

export const EditUserBody: FC<EditUserBodyProps> = ({ 
  currentUserId,
  user: propUser, 
  roles,
  dataAccessRoles,
  open,
  setOpen,
}): JSX.Element => {
  const record: UserResponseDto&RecPatch = useRecordContext()
  const { saving } = useEditContext()
  const save = useSimpleSaveAction()
  const user = record || propUser

  const [states, setStates] = useState({
    selectedRoles: { admin: {}, custom: {}} as RolesSelection,
    selectedDaRoles: {} as Record<number, boolean>,
    active: user.status === UserStatus.Active,
    currTab: 'role' as 'role'|'dataAccessRole',
    error: null as string|null,
  })

  const initialSelection = useMemo(() => {
    const v = {
      selectedRoles: user.roles
        ? user.roles.reduce((acc, r) => {
          if (r.isDefault) {
            acc.admin[r.roleId || -1] = true
          } else {
            acc.custom[r.roleId || -1] = true
          }
          return acc
        }, { admin: {}, custom: {}} as RolesSelection)

        : { admin: {}, custom: {}},
      selectedDaRoles: user.dataAccessRoleIds ? user.dataAccessRoleIds.reduce((acc, rid) => {
        acc[rid] = true
        return acc
      }, {} as any) : {},
    }
    return v
  }, [!!user.roles, !!user.dataAccessRoleIds]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setStates(v => ({...v, ...initialSelection}))
  }, [initialSelection])
  
  useEffect(() => {
    if (!open) {
      setStates(v => ({...v, ...initialSelection, active: user.status === UserStatus.Active, error: null}))
    }
    
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const changeRole = useCallback((role: RoleResponseDto) => (e: ChangeEvent<HTMLInputElement>) => {
    if (!role.roleId) { return; }

    const selections =
      role.isDefault 
      ? { custom: states.selectedRoles.custom, admin: { ...states.selectedRoles.admin, [role.roleId]: e.target.checked} }
      : { admin: states.selectedRoles.admin, custom: { ...states.selectedRoles.custom, [role.roleId]: e.target.checked} }
    setStates(v => ({...v, selectedRoles: selections}))

  }, [states.selectedRoles])

  const changeStatus = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setStates(v => ({...v, active: e.target.checked}))
  }, [])

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleSubmit = () => {
    const input: UpdateUserInput = {
      id: user.id,
      isActive: states.active,
      roleIds: Object.entries(states.selectedRoles.custom).reduce<number[]>((acc, pair) => {
        if (pair[1]) { acc.push(+pair[0]) }
        return acc
      }, []),
      defaultRoleIds: Object.entries(states.selectedRoles.admin).reduce<number[]>((acc, pair) => {
        if (pair[1]) { acc.push(+pair[0]) }
        return acc
      }, []),
      dataAccessRoleIds: Object.entries(states.selectedDaRoles).reduce<number[]>((acc, pair) => {
        if (pair[1]) { acc.push(+pair[0])}
        return acc
      }, [])
    }
    if (states.selectedRoles.admin[7]) { // 7: 'SecureFile Only' role
      if (input.roleIds!.length > 0 || input.defaultRoleIds!.length > 1) {
        setStates(v => ({...v, error: '"SecureFile Only" role can not be assigned together with other roles'}))
        return
      }
    }
    
    setStates(v => ({...v, error: null}))
    save(input, {
      onSuccess: () => {
        setOpen(false)
      },
      onFailure: (err:any) => {
        setStates(v => ({...v, error: retrieveAxiosErr(err)}))
      }
    })
  }

  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const handleTabsChange = (e:any, newValue: any) => {
    setStates(v => ({...v, currTab: newValue}))
  }
  
  return (
    <>
      <Button color="primary" type="button" onClick={handleOpen} >
        Edit
      </Button>
      <Drawer
        anchor="right"
        // onOpen={() => {}}
        onClose={handleClose}
        open={open}
        PaperProps={{ sx: { p: 2, width: width, } }}
      >
        
        <Card sx={{  overflowY: 'auto', mb: 4, }} >
          <CardHeader title="Edit User" />
          <CardContent>
            <Grid container spacing={4} >
              {!user.firstName ? null :
                <Grid item xs={12} >
                  <TextField fullWidth disabled variant="outlined"
                    id="firstname"
                    label="First Name"
                    value={user.firstName || ''}
                  />
                </Grid>}
              {!user.lastName ? null :
                <Grid item xs={12} >
                  <TextField fullWidth disabled variant="outlined"
                    id="lastname"
                    label="Last Name"
                    value={user.lastName || ''}
                  />
                </Grid>}
              <Grid item xs={12} >
                <TextField fullWidth disabled variant="outlined"
                  id="email"
                  label="Email Address"
                  type="email"
                  value={user.username || ''}
                />
              </Grid>
              {!user.phone ? null :
                <Grid item xs={12} >
                  <TextField fullWidth disabled variant="outlined"
                    id="phoneNum"
                    label="Phone Number"
                    value={user.phone || ''}
                  />
                </Grid>}
              <Grid item xs={12} >
                <FormGroup sx={{ '& span.MuiTouchRipple-root': { color: 'primary.main' } }}>
                  <FormControlLabel 
                    color="primary"
                    disabled={user.isSsoEnabled}
                    control={
                      <Switch 
                        disabled={user.userId === currentUserId}
                        checked={states.active}
                        onChange={changeStatus}
                      />} 
                    label={states.active ? 'Active' : 'Inactive'}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} 
                sx={{
                  '.MuiTabPanel-root': { 
                    px: 0, pt: 1.5, bgcolor: theme => theme.palette.background.default, 
                    '& > .MuiFormControl-root': { 
                      width: 'calc(100% - 18px)', 
                      '& > .MuiBox-root': { ml: 1.5, overflowY: 'auto', height: 'calc(100vh - 385px)' },
                      '& .MuiFormGroup-root': { ml: 1.5 },
                    },
                  },
                }}
              >
                {user.isSsoEnabled &&
                  <Box sx={{ px: 2, py: 1, mt: -1, mb: 2,
                    borderRadius: 0.5,
                    border: theme => `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                    bgcolor: theme => alpha(theme.palette.warning.light, 0.1),
                  }}>
                    <Typography variant="caption">
                      When <b>Single Sign-On</b> (SSO) is configured with your organization’s Identity and Access Management (IAM) system, the assignment of Roles and Data Access Roles is managed by your IAM system.
                    </Typography>
                  </Box>}
                <TabContext value={states.currTab}>
                  <Tabs sx={{ mt: -1.5 }}
                    indicatorColor="primary"
                    onChange={handleTabsChange}
                    scrollButtons="auto"
                    textColor="primary"
                    value={states.currTab}
                    variant="scrollable"
                  >
                    <Tab label="Roles" value="role" />
                    <Tab label="Data Access Roles" value="dataAccessRole" />
                  </Tabs>
                  <TabPanel value="role">
                    <FormControl component="fieldset" variant="standard">
                      <Box>
                        <FormGroup>
                          {roles.map(r =>
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  disabled={!user.roles || user.userId === currentUserId}
                                  checked={states.selectedRoles[r.isDefault ? 'admin' : 'custom'][r.roleId || -1] || false} 
                                  onChange={changeRole(r)} 
                                  name={r.name || ''} 
                                />
                              }
                              key={`${r.organizationId}-${r.roleId}` || -1}
                              label={r.name || ''}
                              disabled={user.isSsoEnabled}
                            />
                          )}
                        </FormGroup>
                      </Box>
                    </FormControl>
                  </TabPanel>
                  <TabPanel value="dataAccessRole">
                    <FormControl component="fieldset" variant="standard">
                      <Box>
                        <FormGroup>
                          {dataAccessRoles.map(dar => (
                            <FormControlLabel 
                              control={
                                <Checkbox 
                                  disabled={!user.dataAccessRoleIds}
                                  checked={states.selectedDaRoles[dar.roleId!] ?? false}
                                  onChange={(e) => setStates(v => {
                                    const v1 = {...v}
                                    v1.selectedDaRoles[dar.roleId!] = e.target.checked
                                    return v1
                                  })}
                                  name={dar.name!}
                                />
                              }
                              key={(dar as any).id}
                              label={dar.name!}
                              disabled={user.isSsoEnabled}
                            />
                          ))}
                        </FormGroup>
                      </Box>
                    </FormControl>
                  </TabPanel>
                </TabContext>

              </Grid>
            </Grid>
            {states.error && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {states.error}
                </FormHelperText>
              </Box>
            )}
          </CardContent>
          <Divider />
          <Stack direction="row" justifyContent="space-between" sx={{ p: 2 }} >
            <Button
              color="primary"
              type="button"
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              variant="contained"
              disabled={!open || saving || user.isSsoEnabled}
              onClick={handleSubmit}
            >
              Update
            </Button>
          </Stack>
        </Card>

      </Drawer>
    </>
  )
};

export default EditUser;
